import { sprintf } from 'sprintf-js';

import { Icon } from 'components/Icon/Icon';
import { IdealistLogo } from 'components/Logo/IdealistLogo';
import { useUser } from 'contexts/UserEnvironmentContext';
import {
  pageFooterDataByLocale,
  pageFooterUnauthDataByLocale,
} from 'mappings/PageFooter/pageFooterDataByLocale';
import { homeRoute } from 'routing/routes';
import { trackAnchorClicked } from 'utils/analytics/track/trackAnchorClicked';
import { trackClicked } from 'utils/analytics/track/trackClicked';

import { PageFooterLanguageLinks } from './LanguageLinks/PageFooterLanguageLinks';
import {
  PageFooterBottom,
  PageFooterCopyright,
  PageFooterHolder,
  PageFooterLogoLink,
  PageFooterLogoMissionHolder,
  PageFooterMissionStatement,
  PageFooterSectionExternalLink,
  PageFooterSectionHolder,
  PageFooterSectionLink,
  PageFooterSectionLinksHolder,
  PageFooterSectionTitle,
  PageFooterSectionsAndLogoMissionHolder,
  PageFooterSectionsHolder,
  PageFooterSocialLink,
  PageFooterSocialLinksHolder,
} from './PageFooter.styled';
import {
  pageFooterDataByLinkType,
  pageFooterSocialLinks,
} from './pageFooterConstants';
import { pageFooterOrderedSectionsMetadata } from './pageFooterOrderedSectionsMetadata';

type Props = {
  locale: UserLocale;
  urlByLocale: Record<UserLocale, string | undefined> | undefined;
  showDivider: boolean;
};

export function PageFooter({ locale, urlByLocale, showDivider }: Props) {
  const { user } = useUser();

  const authData = pageFooterDataByLocale[locale];
  const unauthData = pageFooterUnauthDataByLocale[locale];

  const data = user ? authData : unauthData;

  return (
    <PageFooterHolder data-qa-id="footer" $showDivider={showDivider}>
      <PageFooterSectionsAndLogoMissionHolder $showDivider={showDivider}>
        <PageFooterSectionsHolder>
          {pageFooterOrderedSectionsMetadata.map(
            ({ sectionName, desktopOnly }) => {
              const section = data[sectionName];

              return (
                <PageFooterSectionHolder
                  key={sectionName}
                  $desktopOnly={Boolean(desktopOnly)}
                >
                  <PageFooterSectionTitle>
                    {section.title}
                  </PageFooterSectionTitle>

                  <PageFooterSectionLinksHolder>
                    {section.links.map((link) => {
                      const { trackClickedName } =
                        pageFooterDataByLinkType[link.type];

                      if (link.href.startsWith('https://')) {
                        return (
                          <PageFooterSectionExternalLink
                            key={link.title}
                            href={link.href}
                            onClick={() => {
                              trackClicked(trackClickedName, {
                                context: 'Footer',
                              });
                            }}
                            target="_blank"
                          >
                            {link.title}
                          </PageFooterSectionExternalLink>
                        );
                      } else {
                        return (
                          <PageFooterSectionLink
                            key={link.title}
                            to={link.href}
                            onClick={() => {
                              trackClicked(trackClickedName, {
                                context: 'Footer',
                              });
                            }}
                          >
                            {link.title}
                          </PageFooterSectionLink>
                        );
                      }
                    })}
                  </PageFooterSectionLinksHolder>
                </PageFooterSectionHolder>
              );
            },
          )}
        </PageFooterSectionsHolder>

        <PageFooterLogoMissionHolder $showDivider={showDivider}>
          <PageFooterLogoLink
            to={homeRoute.with({ lang: CURRENT_LOCALE() })}
            onClick={() => trackClicked('Footer Logo')}
          >
            <IdealistLogo withText height={56} />
          </PageFooterLogoLink>

          <PageFooterMissionStatement>
            {
              /* Mission statement under logo */
              getText(
                `Working with others, in a spirit of generosity and mutual respect, we want to help build a world where all people can lead free and dignified lives.`,
              )
            }
          </PageFooterMissionStatement>

          <PageFooterSocialLinksHolder>
            {pageFooterSocialLinks[CURRENT_LOCALE()].map(
              ({ href, eventName, iconName }) => (
                <PageFooterSocialLink
                  href={href}
                  key={iconName}
                  onClick={(event) => trackAnchorClicked(event, eventName)}
                  aria-label={eventName}
                >
                  <Icon name={iconName} size={20} colorName="white" />
                </PageFooterSocialLink>
              ),
            )}
          </PageFooterSocialLinksHolder>
        </PageFooterLogoMissionHolder>
      </PageFooterSectionsAndLogoMissionHolder>

      <PageFooterBottom>
        <PageFooterLanguageLinks urlByLocale={urlByLocale} />

        <PageFooterCopyright>
          {sprintf(getText('Copyright © %(year)s Idealist.org'), {
            year: new Date().getFullYear(),
          })}
        </PageFooterCopyright>
      </PageFooterBottom>
    </PageFooterHolder>
  );
}
